import { Component, computed, effect, Signal } from '@angular/core';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { Store } from '@ngxs/store';
import { ActivatedRoute } from '@angular/router';
import { CC_CONSTANTS } from '../../assets/constants';
import { GenericComponent } from '../generic/generic.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, distinctUntilChanged } from 'rxjs';

import { ApexAxisChartSeries, ApexDataLabels, ApexXAxis, ApexYAxis } from 'ng-apexcharts';

import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec3Green,
    colorAec4Orange,
    colorAec5Brown,
    colorAec6Gray,
    colorAec8RedDark,
} from '../../../../../shared/all.constants';
import { CircleMarkerOptions, PathOptions } from 'leaflet';
import { LegendConfig } from '../../../../../shared/components/map/map.component';

import { SidenavService } from '../../../../../shared/services/sidenav.service';
import { convertNumber } from '../../../../../shared/all.helpers';
import {
    DefaultPolygonStyle,
    EmptyPolygonStyle,
    HatchImage,
} from 'src/app/shared/components/map/map.constants';
import {
    LOCALIZATION_MAP_KEY,
    SOURCE_STATIONS_CHART,
    SOURCE_STATIONS_KPIS,
} from '../../assets/keys';

const KEYS = [SOURCE_STATIONS_KPIS, SOURCE_STATIONS_CHART, LOCALIZATION_MAP_KEY];
const color1To500 = '#FFFFB9';
const color10000Plus = '#000000';
interface KpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    // kpi_id=13
    nbSourceStationsSupplying?: number;
    nbSourceStationsSupplyingEvolution?: number;

    // kpi_id=14
    nbSourceStationsIn?: number;
    nbSourceStationsInEvolution?: number;

    // kpi_id=15
    sourceStationsPowerSupplying?: number;
    sourceStationsPowerSupplyingEvolution?: number;

    // kpi_id=2009
    nbTransformersSupplying?: number;
    nbTransformersSupplyingEvolution?: number;
}

interface ChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    nbSourceStationsSupplyingConcession?: number;
    nbSourceStationsInConcession?: number;
    sourceStationsPowerSupplyingConcession?: number;
    error?: boolean;
}

@UntilDestroy()
@Component({
    templateUrl: './sourcestations.component.html',
    styleUrls: ['./sourcestations.component.scss'],
})
export class SourceStationsComponent extends GenericComponent {
    public texts = CC_CONSTANTS.sourceStations.texts;

    public kpis: Signal<KpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(SOURCE_STATIONS_KPIS),
    ) as Signal<KpiData[]>;

    public chartData: Signal<ChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(SOURCE_STATIONS_CHART),
    ) as Signal<ChartData[]>;
    public chartSeries: ApexAxisChartSeries = [];
    public chartTitle = this.texts.sourceStationsChart.title;

    public xaxisCategories: ApexXAxis['categories'];
    public yaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.sourceStationsChart.nbSourceStationsSupplyingConcessionAxisTitle,
            min: 0,
            title: {
                text: this.texts.sourceStationsChart.yaxisTitleLeft,
            },
        },
        {
            seriesName: this.texts.sourceStationsChart.nbSourceStationsSupplyingConcessionAxisTitle,
            min: 0,
        },
        {
            seriesName: this.texts.sourceStationsChart.nbTransformersSupplyingConcessionAxisTitle,
            min: 0,
            opposite: true,
            title: {
                text: this.texts.sourceStationsChart.yaxisTitleRight,
            },
        },
    ];
    public dataLabels: ApexDataLabels = { enabled: true, enabledOnSeries: [] };

    public geometries: Signal<any[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(LOCALIZATION_MAP_KEY),
    ) as Signal<any[]>;

    public legendConfig: LegendConfig[] = [
        {
            title: "Nombre d'usagers",
            values: [
                { color: color1To500, value: '1 - 500' },
                { color: colorAec3Green, value: '500 - 2 000' },
                { color: colorAec1BlueLight, value: '2 000 - 5 000' },
                { color: colorAec2BlueDark, value: '5 000 - 10 000' },
                { color: color10000Plus, value: '10 000+' },
                { color: `url(${HatchImage})`, value: 'Communes hors concession' },
            ],
        },
        {
            title: 'Postes sources alimentant la concession',
            values: [
                { color: '', value: '36 MVA ou moins', size: 5 },
                { color: '', value: 'Entre 40 et 70 MVA (inclus)', size: 8 },
                { color: '', value: 'Entre 72 et 102 MVA (inclus)', size: 12 },
                { color: '', value: '108 MVA ou plus', size: 15 },
                { color: colorAec8RedDark, value: '1 poste source' },
                { color: colorAec5Brown, value: '2 postes sources' },
                { color: colorAec4Orange, value: '3 postes sources ou plus' },
            ],
        },
    ];

    constructor(
        private readonly store: Store,
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
    ) {
        //GenericComponent intialize sideNav, Tabs (if exists), title and subTitle
        super(route, sidenavService, store);

        effect(() => {
            const dataList = this.chartData();
            if (dataList?.length > 0 && !dataList[0].error) {
                this.xaxisCategories = dataList.map((data) => data.xYear);
                this.chartSeries = [
                    {
                        name: this.texts.sourceStationsChart
                            .nbSourceStationsSupplyingConcessionAxisTitle,
                        type: 'column',
                        data: dataList.map((data) => data.nbSourceStationsSupplyingConcession!),
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.sourceStationsChart.nbSourceStationsInConcessionAxisTitle,
                        type: 'column',
                        data: dataList.map((data) => data.nbSourceStationsInConcession!),
                        color: colorAec2BlueDark,
                    },
                    {
                        name: this.texts.sourceStationsChart
                            .nbTransformersSupplyingConcessionAxisTitle,
                        type: 'line',
                        data: dataList.map((data) => data.sourceStationsPowerSupplyingConcession!),
                        color: colorAec3Green,
                    },
                ];

                this.dataLabels.enabledOnSeries = [0, 1, 2];
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });
    }

    public getLocalizationMapStyle(
        feature: GeoJSON.Feature | undefined,
    ): PathOptions | CircleMarkerOptions {
        if (!feature?.properties) {
            return EmptyPolygonStyle;
        }

        if (feature.properties['layerOrder'] === 1) {
            // Cities style
            let fillColor = color1To500;
            const usersCount = feature.properties['usersCount'];

            if (usersCount === null) {
                return EmptyPolygonStyle;
            } else if (usersCount > 10000) {
                fillColor = color10000Plus;
            } else if (usersCount > 5000) {
                fillColor = colorAec2BlueDark;
            } else if (usersCount > 2000) {
                fillColor = colorAec1BlueLight;
            } else if (usersCount > 500) {
                fillColor = colorAec3Green;
            }

            return {
                ...DefaultPolygonStyle,
                fillColor,
            };
        } else {
            // Source stations style
            const sourceStationsCount = feature.properties['sourceStationsCount'];
            let fillColor = colorAec8RedDark;
            if (sourceStationsCount === 2) {
                fillColor = colorAec5Brown;
            } else if (sourceStationsCount > 2) {
                fillColor = colorAec4Orange;
            }

            const totalSourceStationsPower = feature.properties['totalSourceStationsPower'];
            let radius = 5;
            if (totalSourceStationsPower >= 108) {
                radius = 15;
            } else if (totalSourceStationsPower >= 72) {
                radius = 12;
            } else if (totalSourceStationsPower > 36) {
                radius = 8;
            }
            return {
                radius,
                weight: 1,
                fillColor,
                opacity: 1,
                fillOpacity: 0.8,
                color: 'black',
            };
        }
    }

    public getTooltip(feature: GeoJSON.Feature): string {
        if (!feature.properties) {
            return '';
        }
        let { cityName, usersCount, sourceStationsCount, totalSourceStationsPower } =
            feature.properties;

        if (feature.properties['layerOrder'] === 1) {
            // Cities tooltip
            usersCount = convertNumber(Number(usersCount));
            if (usersCount == 0) {
                return `Hors concession`;
            } else {
                return `${cityName} - ${usersCount} usagers`;
            }
        } else {
            // Source stations tooltip
            sourceStationsCount = Number(sourceStationsCount);
            totalSourceStationsPower = Number(totalSourceStationsPower);
            const plural = sourceStationsCount > 1 ? 's' : '';

            return `${cityName} - ${convertNumber(sourceStationsCount)} poste${plural} source${plural}  (${convertNumber(totalSourceStationsPower)} MVA)`;
        }
    }
}
